/** Canonical product contracts. Views are projections; no component owns a competing copy. */
import type { RosterSourceSnapshot, RosterExternalAction } from './roster';
import type { AbsenceRecord } from './absence';
export type Instant = string;
export type BusinessDate = string;
export type Severity = "info" | "warning" | "blocker";
export interface Property {
  id: string;
  name: string;
  timeZone: string;
}
export interface Unit {
  id: string;
  propertyId: string;
  name: string;
  territory: string;
  type: string;
  tags: string[];
  archived: boolean;
  access: "vacant" | "occupied" | "unknown";
  cleanliness: "dirty" | "cleaned" | "inspection-needed" | "ready" | "unknown";
  factUpdatedAt: Instant;
  factSource: string;
  accessUpdatedAt?: Instant;
  disabledInheritedRuleIds: string[];
  disabledRuleReasons?: Record<string, string>;
  notes: string;
  standardMinutes: number;
  permissionGranted?: boolean;
  revision?: number;
  externalIds?: Record<string, string>;
}
export interface Person {
  id: string;
  name: string;
  skills: string[];
  duties: string[];
  archived: boolean;
  externalIds: Record<string, string>;
  revision?: number;
  requiresBuddy?: boolean;
  buddyRestriction?: {from:Instant;until?:Instant};
}
export type EstimateSource = 'unit' | 'service' | 'fixed';
export interface ResolvedEstimate { minutes: number; source: EstimateSource; sourceId: string; sourceRevision: number; label: string; confidence?: 'recorded' | 'unrecorded' }
export interface ServiceDefinition {
  id: string;
  name: string;
  kind: "cleaning" | "extra" | "inspection" | "rework" | "other";
  estimateMinutes: number;
  requiredSkills: string[];
  crewSize: number;
  access: "vacant" | "permission" | "none";
  readinessEffect: "cleaned" | "inspected" | "none";
  estimateSource?: 'unit' | 'service';
}
export interface BookingSegment {
  id: string;
  unitId: string;
  guest: string;
  guestRef?: string;
  source: string;
  externalId?: string;
  revision: number;
  status: "confirmed" | "cancelled" | "no-show";
  arrival: Instant;
  departure: Instant;
  continuityId?: string;
  sourceUpdatedAt?: Instant;
  localOverrides?: Record<string, unknown>;
}
export interface ContinuousStay {
  id: string;
  bookingIds: string[];
  confirmedBy: string;
  confirmedAt: Instant;
  reason: string;
  valid: boolean;
}
export interface RuleTrigger {
  kind:
    | "checkout"
    | "checkin"
    | "calendar"
    | "stay-frequency"
    | "completion"
    | "service-completed";
  interval: number;
  unit: "days" | "weeks" | "months";
  anchorDate?: BusinessDate;
  weekdays?: number[];
  monthDay?: number;
  invalidDay: "skip" | "last-day";
  offset: number;
  offsetUnit: "days" | "hours";
  qualifyingServiceId?: string;
}
export interface RuleDefinition {
  id: string;
  revision: number;
  name: string;
  scope: "property" | "unit";
  scopeId: string;
  serviceId: string;
  enabled: boolean;
  status: "draft" | "active" | "retired";
  effectiveFrom: BusinessDate;
  effectiveTo?: BusinessDate;
  trigger: RuleTrigger;
  conditions: {
    match: "all" | "any";
    weekdays: number[];
    months: number[];
    tags: string[];
    unitTypes: string[];
    minNights?: number;
  };
  window: {
    earliestOffsetHours: number;
    dueOffsetHours: number;
    allowedWeekdays: number[];
    startTime: string;
    endTime: string;
  };
  separateVisit: boolean;
  estimateMinutes?: number;
  estimateSource?: EstimateSource;
  requiredSkills?: string[];
  crewSize?: number;
  priority?: "normal" | "high";
}
export interface EffectiveRule {
  rule: RuleDefinition;
  inherited: boolean;
  disabledHere: boolean;
  reason?: string;
}
export interface RuleOccurrence {
  id: string;
  ruleId: string;
  ruleRevision: number;
  unitId: string;
  serviceId: string;
  contextKey: string;
  nominalDate: BusinessDate;
  earliestStart?: Instant;
  dueAt?: Instant;
  status:
    | "pending"
    | "satisfied"
    | "suppressed"
    | "superseded"
    | "needs-information"
    | "needs-scheduling";
  assignmentId?: string;
  reasons: string[];
  requiredSkills?: string[];
  crewSize?: number;
  stayCycleId?: string;
}
export interface Assignment {
  id: string;
  unitId: string;
  serviceId: string;
  date: BusinessDate | null;
  earliestStart?: Instant;
  dueAt?: Instant;
  estimateMinutes: number;
  remainingMinutes?: number;
  requiredSkills: string[];
  preferredSkills: string[];
  crewSize: number;
  priority: "normal" | "high";
  status:
    "open" | "in-progress" | "paused" | "completed" | "declined" | "cancelled";
  occurrenceIds: string[];
  origin: "rule" | "manual" | "inspection" | "rework";
  notes: string;
  revision: number;
  dependsOn?: string[];
  contextKey: string;
  completedAt?: Instant;
  completionResult?: "passed" | "failed";
  manualDate?: boolean;
  approval?: string;
  estimateSource?: EstimateSource;
  estimateProvenance?: ResolvedEstimate;
  stayCycleId?: string;
  stayCycleRevision?: number;
  followsAssignmentId?: string;
  declineReason?: string;
  manualOverrides?: {
    estimateSource?: EstimateSource;
    earliestStart?: Instant | null;
    dueAt?: Instant | null;
    estimateMinutes?: number;
    requiredSkills?: string[];
    preferredSkills?: string[];
    crewSize?: number;
    priority?: "normal" | "high";
    notes?: string;
  };
}
export interface StaffingPosition {
  id: string;
  requiredSkills: string[];
  duty: string;
  coverageStart: Instant;
  coverageEnd: Instant;
  personId?: string;
}
export interface Shift {
  id: string;
  name: string;
  propertyId: string;
  date: BusinessDate;
  start: Instant;
  end: Instant;
  status: "Draft" | "Planned" | "Active" | "Completed" | "Cancelled";
  assignmentIds: string[];
  positions: StaffingPosition[];
  revision: number;
  comment: string;
  startedAt?: Instant;
  completedAt?: Instant;
  cancelledAt?: Instant;
  cancellationReason?: string;
}
export interface AssignmentAllocation {
  id: string;
  assignmentId: string;
  shiftId: string;
  start?: Instant;
  end?: Instant;
  participantIds: string[];
  status: "tentative" | "committed" | "released" | "finished";
  revision: number;
}
export interface AvailabilityInterval {
  id: string;
  personId: string;
  businessDate: BusinessDate;
  start: Instant;
  end: Instant;
  source: "manual" | "quinyx" | "shift";
  externalId?: string;
  sourceRevision?: string;
  sourceUpdatedAt?: Instant;
  baseStart?: Instant;
  baseEnd?: Instant;
  localOverride?: boolean;
  kind?: "work" | "unavailable";
}
export interface AttendanceEvent {
  id: string;
  personId: string;
  shiftId?: string;
  status: "expected" | "confirmed" | "present" | "absent";
  at: Instant;
  effectiveEnd?: Instant;
  absenceId?: string;
}
export interface ExecutionSession {
  id: string;
  assignmentId: string;
  allocationId?: string;
  personIds: string[];
  start: Instant;
  end?: Instant;
}
export interface LinenEvent {
  id: string;
  unitId: string;
  assignmentId?: string;
  operation: "deliver" | "collect" | "return-check" | "return";
  at: Instant;
  actor: string;
  notes: string;
}
export interface HandoverItem {
  id: string;
  assignmentId: string;
  fromShiftId: string;
  ownerId?: string;
  ownerLabel?: string;
  dueAt?: Instant;
  notes: string;
  status: "open" | "resolved";
}
export interface ChangeProposal {
  id: string;
  shiftId?: string;
  propertyId: string;
  date: BusinessDate;
  subjectType: string;
  subjectId: string;
  title: string;
  before: string;
  after: string;
  action:
    "update-window" | "release-work" | "reassign" | "new-work" | "fact-change";
  assignmentId?: string;
  status: "pending" | "applied" | "kept" | "superseded";
  severity: Severity;
  createdAt: Instant;
  sourceRevision: number;
  reason?: string;
  proposedAssignment?: Assignment;
}
export interface Conflict {
  id: string;
  severity: Severity;
  code: string;
  message: string;
  shiftId?: string;
  assignmentId?: string;
  personId?: string;
  unitId?: string;
}
export interface SyncIntent {
  id: string;
  connector: "hotellinx" | "quinyx";
  unitId?: string;
  assignmentId?: string;
  bookingId?: string;
  sourceRevision: number;
  status: "eligible" | "held" | "failed" | "stale" | "simulated";
  reason: string;
  createdAt: Instant;
  attemptedAt?: Instant;
  bookingRevision?: number;
  fact?: "cleaning-completed" | "guest-ready";
  value?: boolean;
  stayCycleId?: string;
  cycleRevision?: number;
  checkoutId?: string;
  fulfillmentId?: string;
}
export interface ImportRun {
  id: string;
  connector: string;
  name: string;
  at: Instant;
  from: BusinessDate;
  to: BusinessDate;
  mode: "snapshot" | "delta";
  hash: string;
  sourceRevision?: string;
  reconciliationKey?: string;
  added: number;
  updated: number;
  removed: number;
  preserved: number;
  notes: string[];
}
export interface ImportProposal {
  id: string;
  name: string;
  hash: string;
  from: BusinessDate;
  to: BusinessDate;
  mode: "snapshot" | "delta";
  sourceRevision?: string;
  sourceUpdatedAt?: Instant;
  people: Person[];
  intervals: AvailabilityInterval[];
  removeIds: string[];
  counts: {
    added: number;
    updated: number;
    removed: number;
    preserved: number;
  };
  notes: string[];
  baseRevision: number;
  unresolved?: string[];
  reviewedOverrideIds?: string[];
  sourceSnapshot?: RosterSourceSnapshot;
  replaceSourceIds?: string[];
  reconciliationKey?: string;
}
export interface StayCycle {
  id: string;
  unitId: string;
  anchorBookingId: string;
  bookingIds: string[];
  sourceFingerprint: string;
  revision: number;
  serviceRevision: number;
  lastReturnAt?: Instant;
  earlyAuthorization?: { id: string; from: Instant; to?: Instant; reason: string; actor: string; at: Instant; sourceFingerprint: string };
  checkout?: { id: string; at: Instant; actor: string; source: string; serviceRevision: number; sourceFingerprint: string };
}
export interface ServiceFulfillment {
  id: string;
  stayCycleId: string;
  cycleRevision: number;
  assignmentId: string;
  occurrenceIds: string[];
  completedAt: Instant;
  kind: 'cleaning-completed' | 'guest-ready';
  source: 'execution' | 'manual-link' | 'upgrade';
  actor: string;
  ruleIds?: string[];
}
export interface AuditEntry {
  id: string;
  type: string;
  actor: string;
  at: Instant;
  summary: string;
  revision: number;
  targetIds?: string[];
  shiftId?: string;
}
export interface OperationalFact {
  id: string;
  entityType: string;
  entityId: string;
  key: string;
  value: unknown;
  source: string;
  observedAt: Instant;
  recordedAt: Instant;
  revision: number;
}
export interface Workspace {
  schemaVersion: 2;
  id?: string;
  kind?: 'operations' | 'practice';
  practice?: { runId: string; tutorialVersion: number; returnWorkspaceId: string; chapterId: string };
  domainUpgradeVersion?: number;
  fixtureVersion?: string;
  scenarioId?: string;
  fixtureManifest?: { id: string; label: string; purpose: string; propertyId?: string; businessDate?: BusinessDate; participantIds?: string[]; expectedWorkCount?: number };
  activatedAt?: Instant;
  demoClockAt?: Instant;
  revision: number;
  properties: Property[];
  units: Unit[];
  people: Person[];
  services: ServiceDefinition[];
  bookings: BookingSegment[];
  stays: ContinuousStay[];
  rules: RuleDefinition[];
  ruleDrafts: RuleDefinition[];
  occurrences: RuleOccurrence[];
  assignments: Assignment[];
  availability: AvailabilityInterval[];
  attendance: AttendanceEvent[];
  shifts: Shift[];
  allocations: AssignmentAllocation[];
  sessions: ExecutionSession[];
  linen: LinenEvent[];
  impacts: ChangeProposal[];
  syncIntents: SyncIntent[];
  importRuns: ImportRun[];
  audit: AuditEntry[];
  idempotencyKeys: string[];
  legacyArchive: unknown[];
  facts: OperationalFact[];
  ruleHistory?: RuleDefinition[];
  handovers?: HandoverItem[];
  planSessions?: PlanSession[];
  stayCycles?: StayCycle[];
  fulfillments?: ServiceFulfillment[];
  rosterSnapshots?: RosterSourceSnapshot[];
  rosterActions?: RosterExternalAction[];
  absences?: AbsenceRecord[];
  integrationPolicies: {
    hotellinx: {
      enabled: boolean;
      cleaningSync: "checked-out" | "always" | "never";
      inboundOwnership?: Record<string, "source" | "local" | "review">;
    };
  };
}
export interface CommandEnvelope {
  id: string;
  workspaceId?: string;
  type: string;
  expectedRevision?: number;
  actor?: string;
  at?: Instant;
  payload: Record<string, unknown>;
}

export interface PlanningContext { at: Instant; actorId?: string }
export interface OperationalClock { now(): Instant; today(timeZone?: string): BusinessDate; subscribe?(listener: () => void): () => void }
export type ShiftPlanSpec = Pick<Shift, 'id' | 'name' | 'propertyId' | 'date' | 'start' | 'end' | 'assignmentIds' | 'positions' | 'comment'>;
export interface ProposedAllocation { id: string; assignmentId: string; shiftId: string; start?: Instant; end?: Instant; participantIds: string[]; revision?: number }
export interface ExplicitCalendarChange { kind: 'add' | 'update' | 'remove'; intervalId?: string; interval?: AvailabilityInterval; before?: AvailabilityInterval }
export interface PlanPatch { shift: ShiftPlanSpec; allocations: ProposedAllocation[]; releases: string[]; calendarChanges: ExplicitCalendarChange[]; workDates?: { assignmentId: string; date: BusinessDate; reason?: string }[] }
export interface VersionFingerprint { value: string }
export interface PlanEditorState { kind: 'task' | 'person' | 'shift'; id: string; title: string; startDate: string; startTime: string; endDate: string; endTime: string; minutes: string; participantIds: string[]; disambiguation: 'reject' | 'earlier' | 'later'; ambiguous: boolean; error: string }
export interface PlanSession { id: string; workspaceId: string; actorId: string; revision: number; shiftId: string; baseShiftRevision?: number; state: 'editing' | 'applied' | 'discarded'; patch: PlanPatch; fingerprint: VersionFingerprint; updatedAt: Instant; needsRebase?: boolean; editingState?: PlanEditorState }
export interface PlanIssue { code: string; message: string; assignmentId?: string; allocationId?: string; personId?: string }
export interface PlanDiff { type: 'shift' | 'allocation' | 'release' | 'calendar'; id: string; before: unknown; after: unknown; groupId?: string }
export interface PlanAssessment { structuralBlockers: PlanIssue[]; executionGates: PlanIssue[]; warnings: PlanIssue[]; changes: PlanDiff[]; canApply: boolean }
export interface RepairRequest { shiftId: string; assignmentIds?: string[]; fixedAssignmentIds?: string[]; patch?: PlanPatch; sessionId?: string }
export interface RepairGroup { id: string; assignmentIds: string[]; allocationIds: string[]; requiredTogether: true; requiresFullPlan?: boolean }
export interface RepairPreview { workspaceId: string; sessionId?: string; evaluatedAt: Instant; fingerprint: VersionFingerprint; patch: PlanPatch; assessment: PlanAssessment; unresolved: PlanIssue[]; assumptions: string[]; groups: RepairGroup[] }
export interface RuleRange {
  from: BusinessDate;
  to: BusinessDate;
}
export interface RuleEvaluation {
  occurrences: RuleOccurrence[];
  assignments: Assignment[];
  conflicts: Conflict[];
}
export interface RulePreview extends RuleEvaluation {
  baseRevision: number;
  added: Assignment[];
  changed: Assignment[];
  removed: Assignment[];
  unchanged: Assignment[];
  affectedShiftIds: string[];
}
export interface StaffingEstimate {
  laborMinutes: number;
  availableMinutes: number;
  shortageMinutes: number;
  suggestedPeople: number;
  unfilledPositions: number;
  assumptions: string[];
}

export const newId = (prefix: string) =>
  `${prefix}-${globalThis.crypto.randomUUID()}`;
export function emptyWorkspace(): Workspace {
  return {
    schemaVersion: 2,
    revision: 0,
    properties: [],
    units: [],
    people: [],
    services: [],
    bookings: [],
    stays: [],
    rules: [],
    ruleDrafts: [],
    occurrences: [],
    assignments: [],
    availability: [],
    attendance: [],
    shifts: [],
    allocations: [],
    sessions: [],
    linen: [],
    impacts: [],
    syncIntents: [],
    importRuns: [],
    audit: [],
    idempotencyKeys: [],
    legacyArchive: [],
    facts: [],
    integrationPolicies: {
      hotellinx: { enabled: true, cleaningSync: "checked-out" },
    },
  };
}
